'use client'

import { createStyleContext } from '@/lib/create-style-context'
import { styled } from '@/styled-system/jsx'
import { menu } from '@/styled-system/recipes'
import { Menu as ArkMenu, type MenuOpenChangeDetails } from '@ark-ui/react/menu'
import { Portal } from '@ark-ui/react/portal'
import {
  forwardRef,
  useCallback,
  type ComponentProps,
  type ElementRef,
} from 'react'

const { withProvider, withContext } = createStyleContext(menu)

const StyledMenu = withProvider(ArkMenu.Root)

export interface MenuProps extends ComponentProps<typeof StyledMenu> {
  onOpen?: () => void
  onClose?: () => void
}

export const Menu = (props: MenuProps) => {
  const { onOpenChange, onOpen, onClose, ...rest } = props

  const handleOpenChange = useCallback(
    (details: MenuOpenChangeDetails) => {
      onOpenChange?.(details)

      if (details.open) {
        onOpen?.()
      } else {
        onClose?.()
      }
    },
    [onClose, onOpen, onOpenChange]
  )

  return (
    <StyledMenu
      onOpenChange={handleOpenChange}
      lazyMount
      unmountOnExit
      {...rest}
    />
  )
}

const StyledPositioner = withContext(styled(ArkMenu.Positioner), 'positioner')
const StyledContent = withContext(styled(ArkMenu.Content), 'content')
const StyledArrow = withContext(styled(ArkMenu.Arrow), 'arrow')
const StyledArrowTip = withContext(styled(ArkMenu.ArrowTip), 'arrowTip')

export interface MenuContentProps extends ComponentProps<typeof StyledContent> {
  hasArrow?: boolean
}

export const MenuContent = forwardRef<
  ElementRef<typeof StyledContent>,
  MenuContentProps
>((props, ref) => {
  const { children, hasArrow, ...rest } = props

  return (
    <Portal>
      <StyledPositioner>
        <StyledContent ref={ref} {...rest}>
          {hasArrow && (
            <StyledArrow>
              <StyledArrowTip />
            </StyledArrow>
          )}
          {children}
        </StyledContent>
      </StyledPositioner>
    </Portal>
  )
})

MenuContent.displayName = 'MenuContent'

export const MenuContextTrigger = withContext(
  styled(ArkMenu.ContextTrigger),
  'contextTrigger'
)
export const MenuItem = withContext(styled(ArkMenu.Item), 'item')
export const MenuItemGroup = withContext(styled(ArkMenu.ItemGroup), 'itemGroup')
export const MenuItemGroupLabel = withContext(
  styled(ArkMenu.ItemGroupLabel),
  'itemGroupLabel'
)
export const MenuOptionItem = withContext(
  styled(ArkMenu.OptionItem),
  'optionItem'
)
export const MenuSeparator = withContext(styled(ArkMenu.Separator), 'separator')
export const MenuTrigger = withContext(styled(ArkMenu.Trigger), 'trigger')
export const MenuTriggerItem = withContext(
  styled(ArkMenu.TriggerItem),
  'triggerItem'
)

export interface MenuContextTriggerProps
  extends ComponentProps<typeof MenuContextTrigger> {}
export interface MenuItemProps extends ComponentProps<typeof MenuItem> {}
export interface MenuItemGroupProps
  extends ComponentProps<typeof MenuItemGroup> {}
export interface MenuItemGroupLabelProps
  extends ComponentProps<typeof MenuItemGroupLabel> {}
export interface MenuOptionItemProps
  extends ComponentProps<typeof MenuOptionItem> {}
export interface MenuSeparatorProps
  extends ComponentProps<typeof MenuSeparator> {}
export interface MenuTriggerProps extends ComponentProps<typeof MenuTrigger> {}
export interface MenuTriggerItemProps
  extends ComponentProps<typeof MenuTriggerItem> {}
